/**
 * <s-promo-window> — shows time-limited promotion messaging only inside its window.
 *
 * Liquid already skips rendering outside the window; this guards against cached
 * pages and tabs left open: it hides the content the moment the promotion ends, and
 * reveals content rendered hidden ahead of its start (the next promotion) the moment it starts.
 *
 * Attributes:
 *   data-start — ISO 8601 start time
 *   data-end   — ISO 8601 end time (blank = no end)
 */
const MAX_DELAY = 2147483647;

class SPromoWindow extends HTMLElement {
  #startTimer;
  #endTimer;

  connectedCallback() {
    const start = Date.parse(this.dataset.start);
    const end = this.dataset.end ? Date.parse(this.dataset.end) : Infinity;
    const now = Date.now();
    if (Number.isNaN(start) || Number.isNaN(end) || now >= end) {
      this.#hide();
      return;
    }
    if (now < start) {
      this.#hide();
      if (start - now < MAX_DELAY) this.#startTimer = setTimeout(() => this.#show(), start - now);
    } else {
      this.#show();
    }
    if (end !== Infinity && end - now < MAX_DELAY) this.#endTimer = setTimeout(() => this.#hide(), end - now);
  }

  disconnectedCallback() {
    clearTimeout(this.#startTimer);
    clearTimeout(this.#endTimer);
  }

  #show() {
    this.hidden = false;
    this.style.removeProperty('display');
  }

  #hide() {
    this.hidden = true;
    this.style.display = 'none';
  }
}

if (!customElements.get('s-promo-window')) customElements.define('s-promo-window', SPromoWindow);
